import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, Calendar, CheckCircle, XCircle, Plus, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function YesNoField({ label, value, onChange, disabled }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 border-b border-gray-100 last:border-0">
      <p className="text-sm text-gray-900">{label}</p>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          type="button"
          size="sm"
          variant={value === 'yes' ? 'default' : 'outline'}
          className={cn(value === 'yes' && 'bg-green-600 hover:bg-green-700')}
          onClick={() => !disabled && onChange('yes')}
          disabled={disabled}
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1" />
          YES
        </Button>
        <Button
          type="button"
          size="sm"
          variant={value === 'no' ? 'default' : 'outline'}
          className={cn(value === 'no' && 'bg-red-600 hover:bg-red-700')}
          onClick={() => !disabled && onChange('no')}
          disabled={disabled}
        >
          <XCircle className="h-3.5 w-3.5 mr-1" />
          NO
        </Button>
      </div>
    </div>
  );
}

function buildInitialFormData(template) {
  const data = {};
  if (!template) return data;

  for (const field of template.header_fields || []) {
    data[field.id] = field.default || '';
  }

  for (const section of template.sections || []) {
    for (const field of section.fields || []) {
      if (field.type === 'action_plan_table') {
        const rowCount = field.row_count || 3;
        for (let i = 0; i < rowCount; i += 1) {
          for (const col of field.columns || []) {
            data[`${field.id}_${i}_${col.id}`] = '';
          }
        }
      } else if (field.type === 'repeatable_text') {
        const minRows = field.min_rows || 2;
        for (let i = 0; i < minRows; i += 1) {
          data[`${field.id}_${i}`] = '';
        }
      } else {
        data[field.id] = '';
      }
    }
  }

  return data;
}

function getRepeatableRowCount(field, formData) {
  const minRows = field.min_rows || 2;
  const maxRows = field.max_rows || 6;
  let highest = minRows;
  for (let i = 0; i < maxRows; i += 1) {
    if (formData[`${field.id}_${i}`] !== undefined) {
      highest = Math.max(highest, i + 1);
    }
  }
  return Math.min(highest, maxRows);
}

export default function StructuredSupervisionForm({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  employeeRole,
  token,
  onSuccess,
}) {
  const [step, setStep] = useState('choose');
  const [templates, setTemplates] = useState([]);
  const [suggestedTemplateId, setSuggestedTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setSelectedTemplate(null);
    setFormData({});
    setSuggestedTemplateId(null);
    fetchTemplates();
  }, [open, employeeId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = employeeId ? { employee_id: employeeId } : {};
      const response = await axios.get(`${API}/supervision-form-templates`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const loaded = response.data.templates || [];
      setTemplates(loaded);
      setSuggestedTemplateId(response.data.suggested_template_id || null);

      const suggested = loaded.find((tpl) => tpl.template_id === response.data.suggested_template_id);
      if (suggested) {
        startTemplate(suggested);
      }
    } catch (error) {
      console.error('Failed to load supervision templates:', error);
      toast.error('Failed to load supervision forms');
    } finally {
      setLoading(false);
    }
  };

  const startTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData(buildInitialFormData(template));
    setStep('form');
  };

  const updateField = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const sectionCount = useMemo(
    () => (selectedTemplate?.sections || []).length,
    [selectedTemplate],
  );

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/supervisions/structured`,
        {
          template_id: selectedTemplate.template_id,
          form_data: formData,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(`${selectedTemplate.title} saved`);
      onOpenChange(false);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors?.length) {
        toast.error(detail.errors[0]);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error('Failed to save supervision form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderHeaderFields = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border">
      {(selectedTemplate?.header_fields || []).map((field) => (
        <div key={field.id}>
          <Label className="text-xs text-gray-600">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <Input
            type={field.type === 'date' ? 'date' : 'text'}
            value={formData[field.id] || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
            className="mt-1"
            placeholder={field.id.includes('name') ? 'Enter name' : undefined}
          />
        </div>
      ))}
    </div>
  );

  const renderRepeatableField = (field) => {
    const rowCount = getRepeatableRowCount(field, formData);
    const maxRows = field.max_rows || 6;

    return (
      <div key={field.id} className="space-y-3">
        <Label className="text-sm font-medium">{field.label}</Label>
        {Array.from({ length: rowCount }).map((_, rowIdx) => (
          <div key={rowIdx} className="space-y-1">
            <Label className="text-xs text-gray-500">
              {field.row_label || 'Row'} {rowIdx + 1}
              {rowIdx < (field.required_rows || field.min_rows || 0) && (
                <span className="text-red-500 ml-0.5">*</span>
              )}
            </Label>
            <Textarea
              rows={3}
              value={formData[`${field.id}_${rowIdx}`] || ''}
              onChange={(e) => updateField(`${field.id}_${rowIdx}`, e.target.value)}
              placeholder="Status and comments"
            />
          </div>
        ))}
        {rowCount < maxRows && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => updateField(`${field.id}_${rowCount}`, '')}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add another objective
          </Button>
        )}
        {rowCount > (field.min_rows || 2) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => {
              const next = { ...formData };
              delete next[`${field.id}_${rowCount - 1}`];
              setFormData(next);
            }}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Remove last objective
          </Button>
        )}
      </div>
    );
  };

  const renderField = (field) => {
    if (field.type === 'yes_no') {
      return (
        <YesNoField
          key={field.id}
          label={field.label}
          value={formData[field.id]}
          onChange={(v) => updateField(field.id, v)}
        />
      );
    }

    if (field.type === 'repeatable_text') {
      return renderRepeatableField(field);
    }

    if (field.type === 'action_plan_table') {
      const rowCount = field.row_count || 3;
      return (
        <div key={field.id} className="space-y-2">
          <Label className="text-sm font-medium">{field.label}</Label>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border p-2 w-8">#</th>
                  {(field.columns || []).map((col) => (
                    <th key={col.id} className="border p-2 text-left">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }).map((_, rowIdx) => (
                  <tr key={rowIdx}>
                    <td className="border p-2 text-center text-gray-500">{rowIdx + 1}</td>
                    {(field.columns || []).map((col) => (
                      <td key={col.id} className="border p-1">
                        <Input
                          value={formData[`${field.id}_${rowIdx}_${col.id}`] || ''}
                          onChange={(e) => updateField(`${field.id}_${rowIdx}_${col.id}`, e.target.value)}
                          className="h-8 text-xs border-0 shadow-none"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.id} className="space-y-2">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <Textarea
            rows={4}
            value={formData[field.id] || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
          />
        </div>
      );
    }

    return (
      <div key={field.id} className="space-y-2">
        <Label className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Input
          type={field.type === 'date' ? 'date' : 'text'}
          value={formData[field.id] || ''}
          onChange={(e) => updateField(field.id, e.target.value)}
        />
      </div>
    );
  };

  const renderFormBody = () => (
    <div className="space-y-6">
      {renderHeaderFields()}

      {(selectedTemplate?.sections || []).map((section) => (
        <div key={section.id} className="space-y-3 p-4 border rounded-lg bg-white">
          <div>
            <h3 className="font-semibold text-primary text-sm">{section.title}</h3>
            {section.guidance && (
              <p className="text-xs text-gray-500 mt-1">{section.guidance}</p>
            )}
          </div>
          <div className="space-y-3">
            {(section.fields || []).map((field) => renderField(field))}
          </div>
        </div>
      ))}

      <div className="text-center text-xs text-gray-400 py-4 border-t mt-6">
        End of form — all sections above. Review answers, then click Save &amp; generate PDF.
      </div>
    </div>
  );

  const roleHint = employeeRole
    ? `Role on file: ${employeeRole.replace(/_/g, ' ')}`
    : employeeName
      ? `Employee: ${employeeName}`
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] max-h-[90vh] !flex flex-col overflow-hidden p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            {step === 'choose' ? 'Start supervision form' : selectedTemplate?.title}
          </DialogTitle>
          <DialogDescription>
            {step === 'choose'
              ? 'Choose the supervision review form that matches this employee. Complete every section in the app — the PDF is generated from your answers, not a pre-filled Word shell.'
              : `${sectionCount} sections — scroll down for discussion notes, action plans and signatures`}
          </DialogDescription>
        </DialogHeader>

        {step === 'choose' ? (
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {roleHint && (
              <p className="text-sm text-muted-foreground">{roleHint}</p>
            )}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {templates.map((tpl) => (
                  <button
                    key={tpl.template_id}
                    type="button"
                    onClick={() => startTemplate(tpl)}
                    className={cn(
                      'text-left p-4 rounded-xl border-2 transition-colors',
                      tpl.recommended
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-primary hover:bg-primary/5',
                    )}
                    data-testid={`start-supervision-${tpl.template_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-gray-900">{tpl.title}</p>
                      {tpl.recommended && (
                        <Badge className="shrink-0">Recommended</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Structured sections mapped from the approved workspace template
                    </p>
                    <Badge variant="outline" className="mt-3">
                      {tpl.section_count || (tpl.sections || []).length} sections
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="shrink-0 px-6 py-2 border-b bg-slate-50 text-xs text-gray-600 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                <strong>{sectionCount}</strong> sections
              </span>
              {suggestedTemplateId && selectedTemplate?.template_id === suggestedTemplateId && (
                <Badge variant="outline" className="text-[10px]">Recommended for this role</Badge>
              )}
              <span className="text-primary font-medium">Scroll down ↓</span>
            </div>
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4"
              data-testid="structured-supervision-scroll"
            >
              {renderFormBody()}
            </div>
            <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 sm:gap-2 bg-white">
              <Button variant="outline" onClick={() => setStep('choose')} disabled={submitting}>
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-structured-supervision">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save &amp; generate PDF
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
